import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ArrowLeft, Copy, FileText, Mail, Phone, Pencil, Printer, User } from "lucide-react";
import { Button, SegmentedTabs } from "@akuna/ui";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Alert, AlertDescription, AlertTitle } from "@akuna/ui";
import { buildPosologySummary } from "@/lib/prescriptions/posology";
import { Separator } from "@akuna/ui";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";
import { TruncatedTextTooltip } from "@/components/ui/truncated-text-tooltip";
import {
  MedicalRecordForm,
  type MedicalRecordFormHandle,
} from "@/components/medical-records/MedicalRecordForm";
import { useMedicalRecordForm } from "@/components/medical-records/form/use-medical-record-form";
import { MedicalRecordPatientSummaryCard } from "@/components/medical-records/MedicalRecordPatientSummaryCard";
import { MedicalRecordDocumentsSection } from "@/components/medical-records/MedicalRecordDocumentsSection";
import { MedicalRecordExportDialog } from "@/components/medical-records/MedicalRecordExportDialog";
import { PatientTermsPanel } from "@/components/terms/PatientTermsPanel";
import { CustomIcons } from "@/components/icons/CustomIcons";
import { WhatsAppPhoneLink } from "@/components/ui/whatsapp-phone-link";
import { useSmartBack } from "@/lib/navigation-utils";
import { convertMedicalRecordToFormData } from "@/lib/utils/medical-record";
import { formatDateBR, formatDateTimeBR, parseISODateOrDateTime } from "@/lib/date-utils";
import { getLatestPetWeight } from "@/lib/utils/pet-weight";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { usePet } from "@/hooks/usePets";
import { useMedicalRecord } from "@/hooks/useMedicalRecord";
import { useMedicalRecords } from "@/hooks/useMedicalRecords";
import { useExamAttachments } from "@/hooks/useExamAttachments";
import { useHasPermissionSync } from "@/hooks/usePermissions";
import { useIsOwner } from "@/hooks/useIsOwner";
import { useIsSuperAdmin } from "@/hooks/useIsSuperAdmin";
import { useMedicalPrescriptions, type PrescriptionWithItems } from "@/hooks/useMedicalPrescriptions";
import { usePetApplications } from "@/hooks/usePetApplications";
import { useProfile } from "@/hooks/useProfile";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { MedicalRecordMobileLayout } from "@/components/medical-records/mobile/MedicalRecordMobileLayout";
import { MobileMedicalRecordHeader } from "@/components/medical-records/mobile/MobileMedicalRecordHeader";
import { MobilePatientChip } from "@/components/medical-records/mobile/MobilePatientChip";
import { MobilePatientEditFlow } from "@/components/medical-records/mobile/MobilePatientEditFlow";
import { MobileMedicalRecordTabs } from "@/components/medical-records/mobile/MobileMedicalRecordTabs";
import { MobileMedicalRecordActionBar } from "@/components/medical-records/mobile/MobileMedicalRecordActionBar";
import { MobilePatientContextSheet } from "@/components/medical-records/mobile/MobilePatientContextSheet";
import { MobileViewOverflowMenu } from "@/components/medical-records/mobile/MobileViewOverflowMenu";
import { buildMedicalRecordPatientViewModel } from "@/components/medical-records/mobile/medical-record-patient-view-model";
import { resolveMedicalRecordMobileConfig } from "@/components/medical-records/mobile/medical-record-mobile-config";
import type { MedicalRecordPrimaryTab } from "@/components/medical-records/mobile/medical-record-mobile.types";

const formatDate = (value?: string | null) => formatDateBR(value);

type PetVaccinationRow = Database["public"]["Tables"]["pet_applications"]["Row"];

type VaccinationWithRelations = PetVaccinationRow & {
  item?: { name?: string | null; product_subtype?: { name?: string | null } | null } | null;
  vaccines?: { name?: string | null; manufacturer?: string | null } | null;
  vaccine_items?: { name?: string | null } | null;
  appointment?: { starts_at?: string | null } | null;
};

type RelatedMedicalRecord = {
  profiles?: {
    id?: string | null;
    full_name?: string | null;
  } | null;
};

type MedicalRecordRouteTab = "record" | "exams" | "prescriptions" | "applications" | "terms";

const MEDICAL_RECORD_TAB_BY_ROUTE: Record<string, MedicalRecordRouteTab> = {
  record: "record",
  exams: "exams",
  prescriptions: "prescriptions",
  applications: "applications",
  terms: "terms",
  novo: "record",
  exames: "exams",
  prescricao: "prescriptions",
  vacinas: "applications",
  termos: "terms",
};

const ROUTE_TAB_TO_LEGACY: Record<MedicalRecordRouteTab, "novo" | "exames" | "prescricao" | "vacinas" | "termos"> = {
  record: "novo",
  exams: "exames",
  prescriptions: "prescricao",
  applications: "vacinas",
  terms: "termos",
};

const ViewMedicalRecordReadOnly = () => {
  const { t, i18n } = useTranslation("medicalRecords");
  const navigate = useNavigate();
  const smartBack = useSmartBack();
  const { id, recordTab } = useParams<{ id: string; recordTab?: string }>();
  const routeTab = useMemo<MedicalRecordRouteTab | null>(() => {
    if (!recordTab) return null;
    return MEDICAL_RECORD_TAB_BY_ROUTE[recordTab] ?? null;
  }, [recordTab]);
  const activeTab: "novo" | "exames" | "prescricao" | "vacinas" | "termos" = ROUTE_TAB_TO_LEGACY[routeTab ?? "record"];
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const isMobile = useIsMobile();
  const [isMobileContextOpen, setIsMobileContextOpen] = useState(false);
  const [isMobileEditOpen, setIsMobileEditOpen] = useState(false);
  const queryClient = useQueryClient();
  const recordFormRef = useRef<MedicalRecordFormHandle>(null);

  const {
    data: medicalRecord,
    isLoading: isLoadingRecord,
    error: recordError,
  } = useMedicalRecord(id);

  const petId = medicalRecord?.pet_id;
  const { data: pet, isLoading: isLoadingPet, error: petError } = usePet(petId ?? "");
  const { medicalRecords } = useMedicalRecords(petId || undefined);

  const { data: profile, isLoading: isLoadingProfile } = useProfile();
  const { user: currentUser } = useAuth();

  const { hasPermission: hasUpdatePermission, isLoading: isLoadingPermission } = useHasPermissionSync(
    "medical_records",
    "edit",
  );
  const { data: isOwner, isLoading: isLoadingOwner } = useIsOwner();
  const { data: isSuperAdmin, isLoading: isLoadingSuperAdmin } = useIsSuperAdmin();

  const canEdit = useMemo(() => {
    if (
      isLoadingPermission ||
      isLoadingOwner ||
      isLoadingSuperAdmin ||
      !medicalRecord ||
      !currentUser
    ) {
      return false;
    }

    if (!hasUpdatePermission) {
      return false;
    }

    if (isSuperAdmin || isOwner) {
      return true;
    }

    return medicalRecord.vet_user_id === currentUser.id;
  }, [
    hasUpdatePermission,
    isOwner,
    isSuperAdmin,
    medicalRecord,
    currentUser,
    isLoadingPermission,
    isLoadingOwner,
    isLoadingSuperAdmin,
  ]);

  const handleCopyRecordText = useCallback(async () => {
    if (!recordFormRef.current) {
      toast.error(t("actions.copyRecordUnavailable"));
      return;
    }
    const text = recordFormRef.current.getClipboardText();
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t("actions.copyRecordSuccess"));
    } catch {
      toast.error(t("actions.copyRecordError"));
    }
  }, [t]);

  const clinicId = medicalRecord?.clinic_id || profile?.current_clinic_id || "";
  const normalizedClinicId = clinicId || null;

  const {
    attachments: examAttachments,
    isLoading: isLoadingAttachments,
    uploadAttachments,
    deleteAttachment,
    updateAttachment,
  } = useExamAttachments(
    pet?.id,
    undefined,
    normalizedClinicId,
  );

  const { data: prescriptions, isLoading: isLoadingPrescriptions } = useMedicalPrescriptions(
    pet?.id,
    medicalRecord?.appointment_id,
    medicalRecord?.id,
  );

  const { data: vaccinations, isLoading: isLoadingVaccinations } = usePetApplications(pet?.id ?? "");

  const computedPrescriptions = prescriptions ?? [];
  const vaccinationsForRecord = useMemo(() => {
    if (!vaccinations || !medicalRecord?.id) return [];
    const vaccinationRecords = vaccinations as VaccinationWithRelations[];
    return vaccinationRecords.filter((vaccination) => vaccination.medical_record_id === medicalRecord.id);
  }, [vaccinations, medicalRecord?.id]);

  const formDefaultValues = useMemo(() => {
    if (!medicalRecord) return undefined;
    return convertMedicalRecordToFormData(medicalRecord);
  }, [medicalRecord]);

  const formController = useMedicalRecordForm({
    petId: pet?.id ?? petId ?? "",
    appointmentId: medicalRecord?.appointment_id || undefined,
    clinicId,
    defaultValues: formDefaultValues,
    onSubmit: async () => undefined,
    readOnly: true,
  });

  const age = useMemo(() => {
    if (!pet?.birth_date) return null;
    const birthDate = parseISODateOrDateTime(pet.birth_date);
    if (!birthDate) return null;
    return formatDistanceToNow(birthDate, {
      addSuffix: false,
      locale: ptBR,
    });
  }, [pet?.birth_date]);

  const lastMedicalRecord = useMemo(() => {
    if (!medicalRecords?.length) return null;
    return medicalRecords.find((record) => record.id !== id) ?? medicalRecords[0];
  }, [medicalRecords, id]);
  const lastMedicalRecordVetName = (lastMedicalRecord as (typeof lastMedicalRecord & RelatedMedicalRecord) | null)?.profiles?.full_name?.trim() || null;

  const petPhoto = pet?.photo ? (Array.isArray(pet.photo) ? pet.photo[0]?.file_path : pet.photo.file_path) : null;
  const petSubtitle = [pet?.species?.name, pet?.breed?.name, age].filter(Boolean).join(" • ");
  const petAllergies = (pet?.allergies ?? []) as { id: string; condition?: { name?: string | null }; name?: string | null }[];
  const petTemperaments = (pet?.temperaments ?? []) as { id: string; condition?: { name?: string | null }; name?: string | null }[];

  const ownerPrimaryPhone =
    pet?.owner?.phones?.find((phone) => phone.is_primary) ?? pet?.owner?.phones?.[0] ?? null;
  const ownerPrimaryPhoneLabel = ownerPrimaryPhone?.phone_number?.trim()
    ? ownerPrimaryPhone.phone_number
    : null;
  const ownerPrimaryEmail =
    pet?.owner?.emails?.find((email) => email.is_primary)?.email ??
    pet?.owner?.emails?.[0]?.email;
  const responsibleName = medicalRecord?.pets?.owners?.name?.trim() || pet?.owner?.name?.trim() || null;
  const veterinarianName = medicalRecord?.profiles?.full_name?.trim() || null;

  const lastWeight = useMemo(() => {
    return getLatestPetWeight((pet?.weights ?? []) as Array<{ weight: number; measured_at?: string | null; created_at?: string | null; deleted_at?: string | null }>);
  }, [pet?.weights]);

  const petSubtitleInfo = lastWeight?.measured_at
    ? `${lastWeight.weight ? `${lastWeight.weight} kg • ` : ""}${formatDate(lastWeight.measured_at)}`
    : undefined;

  useEffect(() => {
    if (routeTab) return;
    if (!id) return;
    navigate(`/medical-records/${id}/record`, { replace: true });
  }, [id, navigate, routeTab]);

  const handleBack = () =>
    smartBack(pet?.id ? `/patients/pets/${pet.id}/history` : "/patients");

  if (!id) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => smartBack()}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-3xl font-bold text-foreground">{t("notFound")}</h1>
        </div>
      </div>
    );
  }

  if (isLoadingRecord || isLoadingPet || isLoadingProfile) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => smartBack("/patients")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Skeleton className="h-8 w-56" />
        </div>
        <Skeleton className="h-20 w-full" />
        <div className="grid gap-6 lg:grid-cols-[2fr,1fr]">
          <Skeleton className="h-[640px] w-full" />
          <div className="space-y-4">
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (recordError || !medicalRecord || petError || !pet) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => smartBack()}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-3xl font-bold text-foreground">{t("loadErrorTitle")}</h1>
        </div>
        <Card>
          <CardContent className="py-6">
            <p className="text-muted-foreground">
              {t("loadErrorDescription")}
            </p>
            <Button className="mt-4" onClick={() => smartBack()}>
              {t("backToPatients")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const mobileActiveTab: MedicalRecordPrimaryTab = {
    novo: "record",
    exames: "documents",
    prescricao: "prescriptions",
    vacinas: "applications",
    termos: "terms",
  }[activeTab] as MedicalRecordPrimaryTab;
  const mobileConfig = resolveMedicalRecordMobileConfig({ mode: "view", canEdit });
  const mobilePatient = buildMedicalRecordPatientViewModel({
    pet,
    age,
    lastWeight,
    locale: i18n.resolvedLanguage ?? i18n.language,
  });
  const mobileTabs = [
    { value: "record" as const, label: t("tabs.record") },
    { value: "documents" as const, label: t("tabs.exams") },
    { value: "prescriptions" as const, label: t("tabs.prescriptions") },
    { value: "applications" as const, label: t("tabs.applications") },
    { value: "terms" as const, label: t("tabs.terms") },
  ];
  const mobileRouteByTab: Record<MedicalRecordPrimaryTab, string> = {
    record: "record",
    documents: "exams",
    prescriptions: "prescriptions",
    applications: "applications",
    terms: "terms",
  };

  return (
    <>
      <MedicalRecordMobileLayout shouldShowTranscriptionBar={false}
        header={
          <MobileMedicalRecordHeader
            title={t("mobile.titles.view")}
            subtitle={[pet.name, formatDateTimeBR(medicalRecord.consultation_date)].filter(Boolean).join(" · ")}
            backLabel={t("mobile.actions.back")}
            contextLabel={t("mobile.actions.openContext")}
            transcriptionLabel={t("mobile.actions.openTranscription")}
            moreLabel={t("mobile.actions.more")}
            showContext={mobileConfig.showContext}
            showTranscription={mobileConfig.showTranscription}
            showOverflow={mobileConfig.showOverflow}
            onBack={handleBack}
            onOpenContext={() => setIsMobileContextOpen(true)}
            onOpenTranscription={() => undefined}
            overflowMenu={
              <MobileViewOverflowMenu
                moreLabel={t("mobile.actions.more")}
                copyLabel={t("actions.copyRecord")}
                exportLabel={t("actions.export")}
                onCopy={() => void handleCopyRecordText()}
                onExport={() => setExportDialogOpen(true)}
              />
            }
          />
        }
        patient={<MobilePatientChip viewModel={mobilePatient} phoneLabel={t("mobile.actions.callGuardian")} editLabel={t("quickEdit.editPetAriaLabel")} onEdit={() => setIsMobileEditOpen(true)} />}
        tabs={
          <MobileMedicalRecordTabs
            activeTab={mobileActiveTab}
            tabs={mobileTabs}
            onTabChange={(tab) => navigate(`/medical-records/${id}/${mobileRouteByTab[tab]}`)}
          />
        }
        actions={
          <MobileMedicalRecordActionBar
            config={mobileConfig}
            secondaryLabel={t("mobile.actions.back")}
            primaryLabel={t("mobile.actions.edit")}
            editHref={`/medical-records/${id}/edit/${routeTab ?? "record"}`}
            onSecondary={handleBack}
          />
        }
        overlays={
          <MobilePatientContextSheet
            open={isMobileContextOpen}
            onOpenChange={setIsMobileContextOpen}
            title={t("mobile.context.title")}
            description={t("mobile.context.description")}
            closeLabel={t("mobile.context.close")}
            records={medicalRecords ?? []}
            isLoadingRecords={false}
            petId={pet.id}
            ownerId={pet.owner?.id ?? null}
            clinicId={clinicId}
          />
        }
      >
        {isMobile && activeTab === "novo" ? (
          <MedicalRecordForm ref={recordFormRef} formController={formController} onCancel={() => undefined} readOnly variant="mobile" />
        ) : null}
        {activeTab === "exames" ? (
          <MedicalRecordDocumentsSection variant="mobile" clinicId={clinicId} attachments={examAttachments} isLoadingAttachments={isLoadingAttachments} onUploadAttachments={uploadAttachments} onDeleteAttachment={deleteAttachment} onUpdateAttachment={updateAttachment} readOnly={!canEdit} />
        ) : null}
        {activeTab === "prescricao" ? <MedicalRecordPrescriptionsList prescriptions={computedPrescriptions} isLoading={isLoadingPrescriptions} /> : null}
        {activeTab === "vacinas" ? <VaccinationHistoryList vaccinations={vaccinationsForRecord} isLoading={isLoadingVaccinations} /> : null}
        {activeTab === "termos" ? <PatientTermsPanel variant="mobile" petId={pet.id} clinicId={clinicId} medicalRecordId={medicalRecord.id} title={t("termsPanel.title")} description={t("termsPanel.description")} readOnly /> : null}
      </MedicalRecordMobileLayout>
      <div className="hidden md:block space-y-6 pb-32">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">{t("tabs.record")}</h1>
            {(responsibleName || veterinarianName) && (
              <p className="mt-1 text-sm text-muted-foreground">
                {[
                  responsibleName ? t("summary.responsibleName", { name: responsibleName }) : null,
                  veterinarianName ? t("summary.veterinarianName", { name: veterinarianName }) : null,
                ].filter(Boolean).join(" • ")}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => void handleCopyRecordText()}
            disabled={!medicalRecord}
            className="mr-2"
          >
            <Copy className="h-4 w-4 mr-2" />
            {t("actions.copyRecord")}
          </Button>
          <Button
            variant="outline"
            onClick={() => setExportDialogOpen(true)}
            disabled={!medicalRecord}
            className="mr-2"
          >
            <Printer className="h-4 w-4 mr-2" />
            {t("actions.export")}
          </Button>
          {canEdit && (
            <Button
              variant="default"
              onClick={() => navigate(`/medical-records/${id}/edit/${routeTab ?? "record"}`)}
              className="flex items-center gap-2"
            >
              <Pencil className="h-4 w-4" />
              {t("actions.edit")}
            </Button>
          )}
        </div>
      </div>

      <MedicalRecordPatientSummaryCard pet={pet} age={age} lastWeight={lastWeight} />

      <div className="grid gap-4 lg:grid-cols-3 lg:gap-6">
        <div className="min-w-0 space-y-4 order-2 lg:order-none lg:col-span-2 lg:col-start-1 lg:row-start-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">{t("clinicalRecordTitle")}</CardTitle>
            </CardHeader>
            <CardContent>
              <SegmentedTabs
                value={activeTab}
                variant="outline"
                onValueChange={(value) => {
                  const nextValue = value as "novo" | "exames" | "prescricao" | "vacinas" | "termos";
                  const nextRouteTab = Object.entries(ROUTE_TAB_TO_LEGACY).find(([, legacy]) => legacy === nextValue)?.[0] ?? "record";
                  navigate(`/medical-records/${id}/${nextRouteTab}`);
                }}
                fill="screen"
                width="full"
                items={[
                  {
                    value: "novo",
                    label: t("tabs.record"),
                    content: !isMobile ? (
                      <MedicalRecordForm
                        ref={recordFormRef}
                        formController={formController}
                        onCancel={() => { }}
                        isLoading={false}
                        readOnly
                      />
                    ) : null,
                  },
                  {
                    value: "exames",
                    label: t("tabs.exams"),
                    content: (
                      <MedicalRecordDocumentsSection
                        clinicId={clinicId}
                        attachments={examAttachments}
                        isLoadingAttachments={isLoadingAttachments}
                        onUploadAttachments={uploadAttachments}
                        onDeleteAttachment={deleteAttachment}
                        onUpdateAttachment={updateAttachment}
                        readOnly={!canEdit}
                      />
                    ),
                  },
                  {
                    value: "prescricao",
                    label: t("tabs.prescriptions"),
                    content: (
                      <MedicalRecordPrescriptionsList
                        prescriptions={computedPrescriptions}
                        isLoading={isLoadingPrescriptions}
                      />
                    ),
                  },
                  {
                    value: "vacinas",
                    label: t("tabs.applications"),
                    content: (
                      <VaccinationHistoryList
                        vaccinations={vaccinationsForRecord}
                        isLoading={isLoadingVaccinations}
                      />
                    ),
                  },
                  {
                    value: "termos",
                    label: t("tabs.terms"),
                    content: (
                      clinicId ? (
                        <PatientTermsPanel
                          petId={pet.id}
                          clinicId={clinicId}
                          medicalRecordId={medicalRecord.id}
                          title={t("termsPanel.title")}
                          description={t("termsPanel.description")}
                          readOnly
                        />
                      ) : (
                        <Alert>
                          <AlertTitle>{t("alerts.clinicUndefinedTitle")}</AlertTitle>
                          <AlertDescription>{t("alerts.termsClinicUndefined")}</AlertDescription>
                        </Alert>
                      )
                    ),
                  },
                ]}
              />
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4 order-1 lg:order-none lg:col-span-1 lg:col-start-3 lg:row-start-1">
          <Card className="hidden">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg">
                <CustomIcons name="paws" className="h-4 w-4 text-current" />
                {t("patientCard.title")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div className="flex items-center gap-3">
                <Avatar className="h-12 w-12">
                  {petPhoto ? (
                    <AvatarImage src={petPhoto} alt={pet.name} />
                  ) : (
                    <AvatarFallback className="bg-muted/50 text-muted-foreground">
                      <CustomIcons name="paws" className="h-5 w-5 text-current" />
                    </AvatarFallback>
                  )}
                </Avatar>
                <div className="min-w-0 flex-1">
                  <Link
                    to={`/patients/pets/${pet.id}`}
                    className="block truncate text-base font-semibold text-primary hover:underline"
                  >
                    {pet.name}
                  </Link>
                  {petSubtitle && (
                    <p className="mt-0.5 text-sm text-muted-foreground">{petSubtitle}</p>
                  )}
                </div>
              </div>

              {(petAllergies.length > 0 || petTemperaments.length > 0) ? (
                <div className="grid gap-2">
                  {petAllergies.length > 0 ? (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground">{t("patientCard.allergies")}</p>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {petAllergies.map((item) => {
                          const name = item.condition?.name ?? item.name ?? "";
                          if (!name) return null;
                          return (
                            <Badge key={item.id} variant="default">
                              {name}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                  {petTemperaments.length > 0 ? (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground">{t("patientCard.temperament")}</p>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {petTemperaments.map((item) => {
                          const name = item.condition?.name ?? item.name ?? "";
                          if (!name) return null;
                          return (
                            <Badge key={item.id} variant="secondary">
                              {name}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-semibold text-muted-foreground">{t("patientCard.gender")}</p>
                  <p className="text-sm font-medium text-foreground">{pet.gender?.name ?? t("patientCard.notInformedFemale")}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-muted-foreground">{t("patientCard.registered")}</p>
                  <p className="text-sm font-medium text-foreground">{pet.sterilization?.name ?? t("patientCard.notInformedMale")}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-muted-foreground">{t("patientCard.recentWeight")}</p>
                  <p className="text-sm font-medium text-foreground">
                    {lastWeight?.weight ? `${lastWeight.weight} kg` : t("patientCard.notInformedMale")}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-muted-foreground">{t("patientCard.date")}</p>
                  <p className="text-sm font-medium text-foreground">
                    {petSubtitleInfo ?? "-"}
                  </p>
                </div>
              </div>

              {pet.notes ? (
                <div className="rounded-md border bg-muted/30 p-3">
                  <p className="text-xs font-semibold text-muted-foreground">{t("patientCard.patientNotes")}</p>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{pet.notes}</p>
                </div>
              ) : null}

              <Separator />

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm font-semibold text-foreground">{t("patientCard.guardian")}</p>
                </div>
                {pet.owner?.id && pet.owner?.name ? (
                  <Link
                    to={`/patients/owners/${pet.owner.id}`}
                    className="block text-base font-semibold text-primary hover:underline"
                  >
                    {pet.owner.name}
                  </Link>
                ) : (
                  <p className="text-sm text-muted-foreground">{t("patientCard.noGuardian")}</p>
                )}
                <div className="grid gap-3 sm:grid-cols-2 text-sm">
                  <div className="flex items-start gap-2">
                    <Phone className="mt-0.5 h-4 w-4 text-muted-foreground" />
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-muted-foreground">{t("patientCard.phone")}</p>
                      <p className="truncate text-sm text-foreground">
                        {ownerPrimaryPhoneLabel ? (
                          ownerPrimaryPhone?.is_whatsapp ? (
                            <WhatsAppPhoneLink
                              phoneNumber={ownerPrimaryPhoneLabel}
                              className="min-w-0"
                              textClassName="truncate text-sm text-foreground hover:text-primary"
                            />
                          ) : (
                            ownerPrimaryPhoneLabel
                          )
                        ) : (
                          t("patientCard.noPhone")
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Mail className="mt-0.5 h-4 w-4 text-muted-foreground" />
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-muted-foreground">{t("patientCard.email")}</p>
                      <TruncatedTextTooltip
                        text={ownerPrimaryEmail ?? t("patientCard.noEmail")}
                        className="truncate text-sm text-foreground"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {veterinarianName ? (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <User className="h-4 w-4" />
                  {t("summary.veterinarian")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm font-medium text-foreground">{veterinarianName}</p>
              </CardContent>
            </Card>
          ) : null}

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileText className="h-4 w-4" />
                {t("recentHistory.title")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {lastMedicalRecord ? (
                <>
                  <div className="flex items-center justify-between">
                    <p className="text-muted-foreground">{t("recentHistory.lastVisit")}</p>
                    <span className="font-medium">
                      {(() => {
                        const formatted = formatDateTimeBR(lastMedicalRecord.consultation_date);
                        return formatted === "-" ? "-" : formatted.replace(" ", " às ");
                      })()}
                    </span>
                  </div>
                  <Separator />
                  <div>
                    <p className="text-muted-foreground">{t("recentHistory.chiefComplaint")}</p>
                    <p className="font-medium line-clamp-3 whitespace-pre-wrap">{lastMedicalRecord.chief_complaint}</p>
                    {lastMedicalRecordVetName ? (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {t("recentHistory.veterinarianName", { name: lastMedicalRecordVetName })}
                      </p>
                    ) : null}
                  </div>
                  {lastMedicalRecord.treatment && (
                    <>
                      <Separator />
                      <div>
                        <p className="text-muted-foreground">{t("recentHistory.treatment")}</p>
                        <p className="text-muted-foreground">
                          {lastMedicalRecord.treatment.length > 160
                            ? `${lastMedicalRecord.treatment.slice(0, 160)}...`
                            : lastMedicalRecord.treatment}
                        </p>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <p className="text-muted-foreground">{t("recentHistory.empty")}</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <MedicalRecordExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        medicalRecordId={id!}
      />

      <MobilePatientEditFlow
        petId={pet.id}
        ownerId={pet.owner?.id}
        open={isMobileEditOpen}
        onOpenChange={setIsMobileEditOpen}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ["pet", pet.id] });
        }}
      />
      </div>
    </>
  );
};

const MedicalRecordPrescriptionsList = ({
  prescriptions,
  isLoading,
}: {
  prescriptions: PrescriptionWithItems[];
  isLoading: boolean;
}) => {
  const { t } = useTranslation("prescriptions");

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Carregando receitas...</p>;
  }

  if (prescriptions.length === 0) {
    return (
      <Alert>
        <AlertTitle>Nenhuma receita</AlertTitle>
        <AlertDescription>Não há prescrições vinculadas a esta consulta.</AlertDescription>
      </Alert>
    );
  }

  const getPrescriptionTypeLabel = (type?: string | null) => {
    switch (type) {
      case "controlled":
        return "Controlada";
      case "manipulated":
        return "Manipulados";
      case "exam":
        return "Exames";
      case "standard":
      default:
        return "Padrão";
    }
  };

  const getPrescriptionTypeVariant = (type?: string | null): "default" | "secondary" | "destructive" => {
    switch (type) {
      case "controlled":
        return "destructive";
      case "manipulated":
        return "default";
      case "exam":
        return "secondary";
      case "standard":
      default:
        return "secondary";
    }
  };

  return (
    <div className="space-y-4">
      {prescriptions.map((prescription) => (
        <div key={prescription.id} className="rounded-lg border p-4 space-y-3">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold">
                Receita · {formatDateTimeBR(prescription.issued_at)}
              </h3>
              <Badge variant={getPrescriptionTypeVariant(prescription.type)}>
                {getPrescriptionTypeLabel(prescription.type)}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              Veterinário: {prescription.vet?.full_name ?? "Não informado"}
              {prescription.crmv && (
                <span className="ml-1">(CMRV-{prescription.crmv})</span>
              )}
            </p>
          </div>
          <div className="space-y-3 text-sm">
            {prescription.items?.length ? (
              <div>
                <p className="text-xs font-semibold text-muted-foreground">Medicações</p>
                <div className="space-y-2 mt-2">
	                  {prescription.items.map((item) => (
	                    <div key={item.id ?? item.medication_id ?? item.medication_name}>
	                      <p className="font-medium">{item.medication_name || "Medicamento"}</p>
	                      <p className="text-muted-foreground text-xs">
	                        {[item.presentation, item.quantity].filter(Boolean).join(" · ")}
	                      </p>
	                      <p className="text-muted-foreground text-xs">
	                        {buildPosologySummary(item, (key, options) => String(t(`print.${key}`, options)))}
	                      </p>
	                    </div>
	                  ))}
                </div>
              </div>
            ) : null}

            {prescription.exam_items?.length ? (
              <div>
                <p className="text-xs font-semibold text-muted-foreground">Exames</p>
                <div className="space-y-1 mt-2 text-xs text-muted-foreground">
                  {prescription.exam_items.map((item) => (
                    <div key={item.id ?? item.exam_name}>
                      <p>{item.exam_name}</p>
                      {item.instructions && <p>{item.instructions}</p>}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {prescription.observations && (
              <div>
                <Separator />
                <p className="text-muted-foreground text-sm">{prescription.observations}</p>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const VaccinationHistoryList = ({
  vaccinations,
  isLoading,
}: {
  vaccinations: VaccinationWithRelations[];
  isLoading: boolean;
}) => {
  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Carregando aplicações...</p>;
  }

  if (vaccinations.length === 0) {
    return (
      <Alert>
        <AlertTitle>Nenhuma aplicação nesta consulta</AlertTitle>
        <AlertDescription>Não foram registradas aplicações para este atendimento.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {vaccinations.map((vaccination) => {
        const vaccineName =
          vaccination.item?.name ??
          vaccination.vaccines?.name ??
          vaccination.vaccine_items?.name ??
          "Aplicação";
        const applicationDate = formatDateTimeBR(vaccination.application_date);
        const manufacturer = vaccination.vaccines?.manufacturer;

        return (
          <div key={vaccination.id} className="rounded-lg border p-4 space-y-3">
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold">{vaccineName}</h3>
                <span className="text-xs text-muted-foreground">{applicationDate}</span>
              </div>
              {/* {manufacturer && (
                <p className="text-xs text-muted-foreground">
                  {manufacturer}
                </p>
              )} */}
            </div>
            <div className="space-y-2 text-sm">
              <p className="text-muted-foreground">
                Dose {vaccination.dose_number ?? "-"}
              </p>
              {vaccination.batch_number && (
                <p className="text-muted-foreground">Lote: {vaccination.batch_number}</p>
              )}
              {vaccination.next_dose_date && !vaccination.next_dose_completed_at && (
                <p className="text-muted-foreground">
                  Próxima dose:{" "}
                  {(() => {
                    const appointmentStartsAt = vaccination.appointment?.starts_at ?? null;
                    if (appointmentStartsAt) return formatDateTimeBR(appointmentStartsAt);
                    return formatDateBR(vaccination.next_dose_date);
                  })()}
                  {(vaccination as { next_application_item?: { name?: string | null } | null }).next_application_item?.name
                    ? ` · Próxima aplicação: ${(vaccination as { next_application_item?: { name?: string | null } | null }).next_application_item?.name}`
                    : ""}
                </p>
              )}
              {vaccination.adverse_reaction && (
                <p className="text-destructive text-sm">Reação adversa: {vaccination.adverse_reaction}</p>
              )}
              {vaccination.notes && <p className="text-muted-foreground">{vaccination.notes}</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ViewMedicalRecordReadOnly;
